import React, { useState } from 'react';
import { X } from 'lucide-react';
import { appStoreLink } from '@/config/appStoreLink';
import { trackAppStoreTap } from '@/utils/Analytics';
import { isMobileIOS } from '@/utils/browserEnv';
import { daysUntilFor, entryFor } from '@/utils/examSchedule';
import type { UserProfile } from '@/models/UserTypes';

type Variant = 'result' | 'dashboard';

const DISMISS_MS = 14 * 24 * 60 * 60 * 1000;
const dismissKey = (variant: Variant) => `swtpa.appHandoff.${variant}.dismissedAt`;
const DASHBOARD_SHOWN_KEY = 'swtpa.appHandoff.dashboard.shown';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** "November 14" for a future scheduled exam on the target track, else null. */
export function examDayLabel(profile: UserProfile | null | undefined): string | null {
  if (!profile?.targetExam) return null;
  const entry = entryFor(profile, profile.targetExam);
  if (daysUntilFor(entry) === null || !entry?.date) return null;
  const m = entry.date.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}` : null;
}

function readStore(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStore(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Private mode: the card just shows again next time.
  }
}

/** Mobile iOS only; dismissed within 14 days hides it; the dashboard card shows once. */
export function shouldShowAppHandoff(variant: Variant, now = Date.now()): boolean {
  if (!isMobileIOS()) return false;
  if (variant === 'dashboard' && readStore(DASHBOARD_SHOWN_KEY)) return false;
  const dismissedAt = Number(readStore(dismissKey(variant)));
  return !(dismissedAt && now - dismissedAt < DISMISS_MS);
}

export interface AppHandoffCardProps {
  variant: Variant;
  /** Result screen only: the learner's exam day, e.g. "November 14". */
  examDay?: string | null;
  /** Signed-in address; a Gmail address also gets the Continue with Google line. */
  email?: string | null;
}

const isGoogleAddress = (email?: string | null) => /@(gmail|googlemail)\.com$/i.test((email || '').trim());

export const AppHandoffCard: React.FC<AppHandoffCardProps> = ({ variant, examDay, email }) => {
  const [visible, setVisible] = useState(() => {
    const show = shouldShowAppHandoff(variant);
    if (show && variant === 'dashboard') writeStore(DASHBOARD_SHOWN_KEY, String(Date.now()));
    return show;
  });
  if (!visible) return null;

  const placement = variant === 'result' ? 'result_card' : 'dashboard_card';
  const href = appStoreLink(placement);
  const dismiss = () => {
    writeStore(dismissKey(variant), String(Date.now()));
    setVisible(false);
  };

  return (
    <div className="card relative space-y-3" data-testid={`app-handoff-${variant}`}>
      {variant === 'result' ? (
        <>
          <h2 className="heading-3">Your result is saved. Keep going in the app.</h2>
          <p className="body-base">
            {examDay ? `One question a day until ${examDay}` : 'One question a day'}, your exam countdown, and a nudge
            on the days you&apos;d skip. In the app, tap I already have an account, then Forgot password to set a
            password for this email.{isGoogleAddress(email) ? ' Continue with Google works too.' : ''} You pick up
            right here.
          </p>
        </>
      ) : (
        <>
          <button type="button" onClick={dismiss} aria-label="Dismiss" className="absolute right-4 top-4 text-neutral-500">
            <X className="h-5 w-5" />
          </button>
          <h2 className="heading-3 pr-8">Study from your phone.</h2>
          <p className="body-base">
            Same account, same progress. The app can remind you before a day slips. No password yet? Tap Forgot
            password in the app to set one for this email.
          </p>
        </>
      )}
      <a href={href} onClick={() => trackAppStoreTap(placement)} className="btn-primary w-full" data-testid="app-handoff-get-app">
        Get the iOS app
      </a>
      {variant === 'result' && (
        <button
          type="button"
          onClick={dismiss}
          className="w-full py-2 text-sm font-semibold text-primary-700 underline-offset-2 hover:underline"
          data-testid="app-handoff-stay-web"
        >
          Stay on the web
        </button>
      )}
    </div>
  );
};
